import type { MediaItem, MediaType } from '@/types/media';

export type SortKey = 'latest' | 'hot' | 'rating';

export interface FilterState {
  genre: string;
  region: string;
  year: string;
  rating: string;
}

export const DEFAULT_FILTER: FilterState = {
  genre: '全部',
  region: '全部',
  year: '全部',
  rating: '全部',
};

export const RATING_THRESHOLD: Record<string, number> = {
  '9.0 分以上': 9,
  '8.5 分以上': 8.5,
  '8.0 分以上': 8,
  '7.5 分以上': 7.5,
};

function matchGenre(item: MediaItem, genre: string) {
  if (genre === '全部') return true;
  return item.genres.some((g) => g.includes(genre) || genre.includes(g));
}

function matchRegion(item: MediaItem, region: string) {
  if (region === '全部') return true;
  return item.region === region;
}

function matchYear(item: MediaItem, year: string) {
  if (year === '全部') return true;
  if (year === '更早') return item.year <= 2021;
  return item.year === Number(year);
}

function matchRating(item: MediaItem, rating: string) {
  if (rating === '全部') return true;
  const threshold = RATING_THRESHOLD[rating];
  if (threshold === undefined) return true;
  return item.rating >= threshold;
}

export function filterMedia(list: MediaItem[], filter: FilterState): MediaItem[] {
  return list.filter(
    (item) =>
      matchGenre(item, filter.genre) &&
      matchRegion(item, filter.region) &&
      matchYear(item, filter.year) &&
      matchRating(item, filter.rating),
  );
}

export function sortMedia(list: MediaItem[], sort: SortKey): MediaItem[] {
  const copy = [...list];
  if (sort === 'latest') copy.sort((a, b) => b.year - a.year || b.popularity - a.popularity);
  if (sort === 'hot') copy.sort((a, b) => b.popularity - a.popularity);
  if (sort === 'rating') copy.sort((a, b) => b.rating - a.rating);
  return copy;
}

export function mediaByType(list: MediaItem[], type: MediaType): MediaItem[] {
  return list.filter((item) => item.type === type);
}

export function searchMedia(list: MediaItem[], keyword: string): MediaItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return list.filter((item) => {
    const haystack = [item.title, item.director, item.region, item.synopsis, ...item.genres, ...item.cast]
      .join(' ')
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function relatedMedia(list: MediaItem[], current: MediaItem, limit = 6): MediaItem[] {
  return list
    .filter((item) => item.id !== current.id)
    .map((item) => {
      let score = 0;
      if (item.type === current.type) score += 2;
      score += item.genres.filter((g) => current.genres.includes(g)).length * 2;
      if (item.region === current.region) score += 1;
      return { item, score };
    })
    .sort((a, b) => b.score - a.score || b.item.popularity - a.item.popularity)
    .slice(0, limit)
    .map((entry) => entry.item);
}